import { http } from '../http';
import { dataFetchingClient, registerQuery, useQuery } from '../../utils/DataFetchingClient';
import { quickFetchOrGet, getCachedThenNew } from '../../utils/ajaxUtils';
import { BASE_URI_LC } from '../../constants/ApiConstants';
import getLang from 'I18n/utils/getLang';
import { useMemo } from 'react';
import { updateFullLessonCache, updatePartialLessonCache } from '../lessons';
import { useAllProgress } from '../userProgress';
import { useIsPartner } from '../partners';
import pick from 'hs-lodash/pick';

const normalizeLesson = lesson => Object.assign({
  contentIds: lesson.content.map(content => content.id),
  isRequesting: false,
  hasError: false
}, pick(lesson, ['deprecationCta', 'deprecationFinalDate', 'deprecationMessage', 'deprecationWarningDate', 'description', 'duration', 'hsSoftwareFilters', 'id', 'internalName', 'language', 'lessonType', 'levelFilters', 'name', 'previewThumbnailRectangle', 'previewThumbnailRectangleUrl', 'previewThumbnailSquare', 'previewThumbnailSquareUrl', 'subCategories', 'visibility']));

export const GET_TRACK = registerQuery({
  fieldName: 'track',
  args: ['trackId', 'language'],

  fetcher({
    trackId,
    language
  }) {
    return quickFetchOrGet(`${BASE_URI_LC}/tracks/${trackId}/${language.toUpperCase()}`).then(response => response);
  }

});
export const fetchTrackById = (trackId, language) => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_TRACK,
    variables: {
      trackId,
      language
    }
  }).then(track => {
    track.lessons.forEach(lesson => {
      updateFullLessonCache(lesson, language);
      updatePartialLessonCache(normalizeLesson(lesson), language);
    });
    return track;
  });
};
export const useFullTrack = (trackId, language = getLang()) => {
  if (!language || !language.length) language = getLang();
  const {
    data: track,
    loading: trackLoading,
    error: trackError
  } = useQuery(GET_TRACK, {
    variables: {
      trackId: trackId.toString(),
      language
    },
    skip: !trackId || isNaN(Number(trackId)),

    onCompleted(data) {
      data.track.lessons.forEach(lesson => {
        updateFullLessonCache(lesson, language);
        updatePartialLessonCache(normalizeLesson(lesson), language);
      });
    }

  });
  return {
    track,
    trackLoading,
    trackError
  };
};
export const useTrackLessons = (trackId, language = getLang()) => {
  const {
    track,
    trackLoading,
    trackError
  } = useFullTrack(Number(trackId), language);
  return {
    trackLessons: track ? track.lessons : null,
    trackLessonsLoading: trackLoading,
    trackLessonsError: trackError
  };
};
export const useTrackPracticalExercises = (trackId, language = getLang()) => {
  const {
    trackLessons,
    trackLessonsLoading,
    trackLessonsError
  } = useTrackLessons(trackId, language);
  const trackPracticalExercises = useMemo(() => {
    if (!trackLessons) return null;
    const practicalExercises = [];
    trackLessons.forEach(lesson => lesson.content.forEach(content => {
      if (content.contentType === 'PRACTICAL_EXERCISE_FLOW') {
        practicalExercises.push(Object.assign({}, content, {
          parentLesson: lesson
        }));
      }
    }));
    return practicalExercises;
  }, [trackLessons]);
  return {
    trackPracticalExercises,
    trackPracticalExercisesLoading: trackLessonsLoading,
    trackPracticalExercisesError: trackLessonsError
  };
};
export const useCanDoAllTrackPracticalExercises = (trackId, language = getLang()) => {
  const {
    trackPracticalExercises,
    trackPracticalExercisesLoading,
    trackPracticalExercisesError
  } = useTrackPracticalExercises(trackId, language);
  const canDoAllTrackPracticalExercises = useMemo(() => {
    if (!trackPracticalExercises) return true;
    return !trackPracticalExercises.some(exercise => exercise.requiredAccess && exercise.requiredAccess.length || exercise.requiredProducts && exercise.requiredProducts.length);
  }, [trackPracticalExercises]);
  return {
    canDoAllTrackPracticalExercises,
    canDoAllTrackPracticalExercisesLoading: trackPracticalExercisesLoading,
    canDoAllTrackPractcialExercisesError: trackPracticalExercisesError
  };
};
export const useTrackPendingPracticalExercises = (trackId, language = getLang()) => {
  const {
    trackPracticalExercises,
    trackPracticalExercisesLoading,
    trackPracticalExercisesError
  } = useTrackPracticalExercises(trackId, language);
  const {
    allProgress,
    allProgressLoading,
    allProgressError
  } = useAllProgress({
    language
  });
  const pendingPracticalExercises = useMemo(() => {
    if (!trackPracticalExercises || !allProgress) return null;
    return trackPracticalExercises.filter(exercise => {
      return exercise.parentLesson.graded && exercise.graded && (!allProgress.contentProgress[exercise.id] || !allProgress.contentProgress[exercise.id].completed);
    });
  }, [allProgress, trackPracticalExercises]);
  return {
    pendingPracticalExercises,
    pendingPracticalExercisesLoading: trackPracticalExercisesLoading || allProgressLoading,
    pendingPracticalExercisesError: trackPracticalExercisesError || allProgressError
  };
};
export const GET_TRACK_AVAILABLE_LANGUAGES = registerQuery({
  fieldName: 'trackAvailableLanguages',
  args: ['trackId'],

  fetcher({
    trackId
  }) {
    return http.get(`${BASE_URI_LC}/tracks/${trackId}/available-languages`).then(response => response).then(data => data.map(lang => lang.toUpperCase()));
  }

});
export const fetchAvailableLanguagesByTrackId = trackId => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_TRACK_AVAILABLE_LANGUAGES,
    variables: {
      trackId
    }
  });
};
export const useAvailableLanguagesByTrack = trackId => {
  const {
    data: trackLanguages,
    loading: trackLanguagesLoading,
    error: trackLanguagesError
  } = useQuery(GET_TRACK_AVAILABLE_LANGUAGES, {
    variables: {
      trackId
    }
  });
  return {
    trackLanguages,
    trackLanguagesLoading,
    trackLanguagesError
  };
};
export const GET_ALL_PUBLIC_TRACKS = registerQuery({
  fieldName: 'allPublicTracks',
  args: ['language'],

  fetcher({
    language
  }) {
    const lang = language || getLang();
    return getCachedThenNew(`tracks-${lang}`, `${BASE_URI_LC}/tracks/${lang}?fallbackLanguage=en`).then(response => response);
  }

});
export function fetchAllPublicTracks(language) {
  const lang = language || getLang();
  return dataFetchingClient.queryUnwrapped({
    query: GET_ALL_PUBLIC_TRACKS,
    variables: {
      language: lang
    },
    fetchPolicy: 'network-only'
  });
}
export const useAllPublicTracks = (language, skip = false) => {
  const {
    data: allPublicTracks,
    loading: allPublicTracksLoading,
    error: allPublicTracksError
  } = useQuery(GET_ALL_PUBLIC_TRACKS, {
    variables: {
      language
    },
    skip
  });
  return {
    allPublicTracks: allPublicTracks ? allPublicTracks.result : null,
    allPublicTracksLoading,
    allPublicTracksError
  };
};
const GET_ALL_PARTNER_TRACKS = registerQuery({
  fieldName: 'allPartnerTracks',
  args: ['language'],

  fetcher({
    language
  }) {
    return quickFetchOrGet(`partner-tracks-${language}`, `${BASE_URI_LC}/tracks/partners/${language}?fallbackLanguage=en`).then(response => {
      return response;
    });
  }

});
export const fetchAllPartnerTracks = ({
  language
}) => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_ALL_PARTNER_TRACKS,
    variables: {
      language
    }
  });
};
export const useAllPartnerTracks = (language, skip = false) => {
  const isPartner = useIsPartner();
  const {
    data: allPartnerTracks,
    loading: allPartnerTracksLoading,
    error: allPartnerTracksError
  } = useQuery(GET_ALL_PARTNER_TRACKS, {
    variables: {
      language
    },
    skip: !isPartner || skip
  });
  return {
    allPartnerTracks,
    allPartnerTracksLoading,
    allPartnerTracksError
  };
};
export const useAllTracks = (language = getLang(), skip = false) => {
  const {
    allPublicTracks,
    allPublicTracksLoading,
    allPublicTracksError
  } = useAllPublicTracks(language, skip);
  const {
    allPartnerTracks,
    allPartnerTracksLoading,
    allPartnerTracksError
  } = useAllPartnerTracks(language, skip);
  const allTracks = useMemo(() => {
    const allTracksArray = [...(Array.isArray(allPublicTracks) ? allPublicTracks : []), ...(Array.isArray(allPartnerTracks) ? allPartnerTracks : [])];
    return allTracksArray.reduce((currentTracks, track) => {
      currentTracks[track.id] = track;
      return currentTracks;
    }, {});
  }, [allPartnerTracks, allPublicTracks]);
  return {
    allTracks,
    allTracksLoading: allPublicTracksLoading || allPartnerTracksLoading,
    allTracksError: allPublicTracksError || allPartnerTracksError
  };
};
export const useTrack = (trackId, language = getLang(), skip = false) => {
  if (!language || !language.length) language = getLang();
  const {
    allTracks,
    allTracksLoading: trackLoading,
    allTracksError: trackError
  } = useAllTracks(language, skip);
  return {
    track: allTracks && allTracks[trackId],
    trackLoading,
    trackError
  };
};